import { Pagination, Zone, ZonePayload } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit, Receipt } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import Permissions from 'src/Common/Permissions';
import { ZonePanel } from './ZonePanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ObjectField, { ObjectFieldWrapperProps } from 'src/Common/Components/DataTable/ObjectField';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import { useNavigate } from 'react-router';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

const ROW_MIN_HEIGHT = 64;

// Wraps ObjectField entries in a ScrollableStack so long strategy/pool names
// scroll horizontally rather than overflowing out of their proportional cell.
const ScrollableFieldWrapper = (props: ObjectFieldWrapperProps): React.JSX.Element => (
  <ScrollableStack minHeight={ROW_MIN_HEIGHT} width="100%" {...props} />
);

export const ZoneManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { scrollTop } = useNavigation();
  const navigate = useNavigate();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();
  const [, startRequest] = useRequest();

  const [forceDelete, setForceDelete] = useState<boolean>(false);
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedZone, setSelectedZone] = useState<Zone>();

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setSelectedZone(undefined);
    setDialogOpen(false);
    setForceDelete(false);
  }, []);

  const showPanel = useCallback((zone?: Zone) => {
    dismiss();
    setSelectedZone(zone);
    scrollTop();
    setPanelOpen(true);
  }, []);

  const saveZone = useCallback(
    async (payload: ZonePayload, allowOverwrite: boolean) => {
      startRequest(
        () => api.saveZone(payload, allowOverwrite),
        () => {
          enqueueSnackbar('Zone saved');
          dismiss();
          refresh();
        },
      );
    },
    [api.saveZone],
  );

  const deleteZone = useCallback(() => {
    if (selectedZone === undefined) {
      return;
    }

    startRequest(
      () => api.deleteZone(selectedZone.coordinate, selectedZone.name, forceDelete),
      () => {
        enqueueSnackbar('Zone deleted');
        dismiss();
        refresh();
      },
    );
  }, [selectedZone, forceDelete, api.deleteZone]);

  const fetchItems = useCallback((pagination: Pagination) => api.getZones(pagination), [api]);

  const columns: ColumnDef[] = [
    {
      header: 'Name',
      body: (z: Zone) => z.name,
      sortField: 'Z.name',
      filterField: 'name',
      fitContent: true,
    },
    {
      header: 'Coordinate',
      body: (z: Zone, searchText?: string) => (
        <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
          {searchText ? dataTableHighlight(z.coordinate ?? '', [searchText]) : z.coordinate}
        </ScrollableStack>
      ),
      sortField: 'N.materializedPath',
      filterField: 'coordinate',
      proportionalContent: '30%',
    },
    {
      header: 'Source',
      body: (zone: Zone) => (
        <ObjectField
          direction={'column'}
          fields={[
            {
              key: 'Pool',
              value: zone.sourcePool?.name ?? '-',
            },
            {
              key: 'Pattern',
              value: zone.sourceConfirmationPattern,
            },
            {
              key: 'Optional',
              value: zone.sourceOptionalConfirmation,
            },
          ]}
          extraEntryStyle={{ margin: 8 }}
        />
      ),
      fitContent: true,
    },
    {
      header: 'Destination',
      body: (zone: Zone) => (
        <ObjectField
          direction={'column'}
          fields={[
            {
              key: 'Pool',
              value: zone.destinationPool?.name ?? '-',
            },
            {
              key: 'Pattern',
              value: zone.destinationConfirmationPattern,
            },
            {
              key: 'Optional',
              value: zone.destinationOptionalConfirmation,
            },
          ]}
          extraEntryStyle={{ margin: 8 }}
        />
      ),
      fitContent: true,
    },
    {
      header: 'Orders',
      body: (zone: Zone) => {
        const { maxParallelOpenOrders, autoConclude, autoDelete } = zone;
        return (
          <ObjectField
            direction={'column'}
            fields={[
              {
                key: 'Max Open',
                value: maxParallelOpenOrders === 0 ? '∞' : maxParallelOpenOrders,
                tooltip:
                  maxParallelOpenOrders === 0
                    ? 'Amount of open orders is not limited'
                    : 'Amount of open orders is limited to: ' + maxParallelOpenOrders.toString(),
              },
              {
                key: 'Auto Conclude',
                value: autoConclude,
              },
              {
                key: 'Auto Delete',
                value: autoDelete,
              },
            ]}
            extraEntryStyle={{ margin: 8 }}
          />
        );
      },
      fitContent: true,
    },
    {
      header: 'Strategies',
      body: (zone: Zone) => (
        <ObjectField
          direction={'column'}
          fields={[
            {
              key: 'Confirmation',
              value: zone.confirmationStrategy.name,
            },
            {
              key: 'Notification',
              value: zone.notificationStrategy.name,
            },
            {
              key: 'Merge',
              value: zone.mergeStrategy.name,
            },
          ]}
          useWrapper={ScrollableFieldWrapper}
          extraEntryStyle={{ margin: 8 }}
        />
      ),
      proportionalContent: '70%',
    },
  ];

  return (
    <ContentControl
      panel={<ZonePanel zone={selectedZone} saveHandler={saveZone} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          text={
            <>
              {forceDelete && (
                <>
                  <Typography color={'error'}>{'open orders found'}</Typography>
                  <Typography color={'primary'}>{'delete anyway?'}</Typography>
                </>
              )}
              {!forceDelete && <Typography color={'primary'}>{'confirm deletion'}</Typography>}
            </>
          }
          title={`Delete "${selectedZone?.name ?? ''}" ?`}
          onClose={dismiss}
          onConfirm={deleteZone}
          timedOkButton={forceDelete}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Picking Zone'}
          onClick={() => {
            showPanel(undefined);
          }}
          requiredPermission={Permissions.ZONES_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchItems}
        columns={columns}
        actions={[
          {
            title: 'Blueprints',
            icon: <Receipt />,
            onClick: (z: Zone) => {
              // @todo: navigate to all blueprints instead, with filter
              // then delete the base blueprint view
              navigate('/picking/blueprints/' + z.name + '/' + z.coordinate);
            },
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (z: Zone) => showPanel(z),
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (z: Zone) => {
              setSelectedZone(z);
              setDialogOpen(true);
            },
          },
        ]}
        maxExpandedActions={3}
      />
    </ContentControl>
  );
};
